<template>
  <div class="sp-text-field">
    <sp-label v-if="label" ref="labelElement" for="sp-text-field-input" block>{{ label }}</sp-label>
    <div ref="root" class="sp-text-field__control" :class="classModifiers">
      <div class="sp-text-field-content">
        <div class="sp-text-field-prepend" @click="onPrependClick">
          <slot name="prepend">
            <sp-icon
              v-if="prependIcon"
              :name="prependIcon"
              class="sp-text-field-content__icon"
              part="prepend-icon"
            ></sp-icon>
          </slot>
        </div>
        <div class="sp-text-field-content__input-container">
          <slot />
          <input
            id="sp-text-field-input"
            ref="input"
            class="sp-text-field-content__input-field"
            :data-testid="inputTestId"
            :aria-label="ariaLabel"
            :aria-describedby="describedBy"
            :type="type"
            :value="model"
            :placeholder="placeholder"
            :disabled="disabled"
            :readonly="readonly"
            :focus-on-open="focusOnOpenAttribute"
            :inputmode="inputmode"
            :required="required"
            @input.stop.prevent="onInput"
            @focus="onFocus"
            @blur="onBlur"
          />
        </div>
        <div class="sp-text-field-append-container">
          <div v-if="isClearVisible" class="sp-text-field-clear" @click.stop="onClearClick">
            <slot name="clear" class="clear-slot">
              <sp-button
                class="clear-button"
                size="small"
                color="surface"
                :icon="clearIcon"
                :densitysmcompact="densitysmcompact"
              ></sp-button>
            </slot>
          </div>
          <div class="sp-text-field-append" @click="onAppendClick">
            <slot name="append" class="append-slot">
              <sp-icon
                v-if="appendIcon"
                :name="appendIcon"
                class="sp-text-field-content__icon"
                part="append-icon"
              ></sp-icon>
            </slot>
          </div>
        </div>
      </div>

      <sp-progress v-if="loading" type="linear" size="micro" class="sp-text-field__loader"></sp-progress>
    </div>

    <div v-if="shownMessages.length > 0" :id="MESSAGES_ID" class="sp-text-field-messages" part="messages">
      <span v-for="(message, index) in shownMessages" :key="index" class="sp-text-field-messages__message">{{
        message
      }}</span>
    </div>
  </div>
</template>

<script setup>
import { useElementHover } from "@vueuse/core";
import { computed, onMounted, ref, toRef, useTemplateRef, watchPostEffect } from "vue";
import { useAriaLabel } from "#ce/composables/useAriaLabel";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useExpose } from "#ce/composables/useExpose";
import { useForm } from "#ce/composables/useForm";
import { useFormControl } from "#ce/composables/useFormControl";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { useTextModel } from "#ce/composables/useTextModel";
import { isPresent, toArray, toBoolean } from "#ce/utils/props";
import { formAssociatedHost } from "#ce/framework/utils";
import { props as propsDefinitions } from "./props.js";

// sp-text-field is a genuine form control: submit a value, match :disabled, respond to <fieldset disabled>.
defineOptions(formAssociatedHost({ shadowRootOptions: { delegatesFocus: true } }));

const emit = defineEmits(["input", "click-append", "click-prepend", "click-clear", "input-initialized"]);
const { emitBubbling } = useHostEmit();

const props = defineProps(propsDefinitions);

const VALIDITY_FLAGS = [
  "badInput",
  "customError",
  "patternMismatch",
  "rangeOverflow",
  "rangeUnderflow",
  "stepMismatch",
  "tooLong",
  "tooShort",
  "typeMismatch",
  "valueMissing",
];

/**
 * The root element of the component.
 * @type {import('vue').Ref<HTMLElement|null>}
 */
const root = useTemplateRef("root");
const isHovered = useElementHover(root);

/**
 * The input element of the component.
 * @type {import('vue').Ref<HTMLInputElement|null>}
 */
const input = useTemplateRef("input");

const loading = computed(() => toBoolean(props.loading));
// Disabled is governed by the form-control composable: presence-based own
// attribute plus ancestor <fieldset disabled>, not a standalone toBoolean(prop).
const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });
const readonly = computed(() => isPresent(props.readonly));
// A bare attribute while set, absent otherwise: sp-dialog tests for its presence.
const focusOnOpenAttribute = computed(() => (isPresent(props.focusOnOpen) ? "" : null));
const required = computed(() => isPresent(props.required));

const propagateAppendClick = computed(() => toBoolean(props.propagateAppendClick));

const clearable = computed(() => toBoolean(props.clearable));
const persistentClear = computed(() => toBoolean(props.persistentClear));

// An absent value is `null` rather than the empty string, so `defaultValue` reports "no default"
// distinctly from a default of "".
function toValueOrNull(value) {
  return value ?? null;
}

const {
  text: model,
  defaultValue,
  setText,
  setUserText,
  reset,
} = useTextModel({
  normalizeDefault: toValueOrNull,
});

const { exposeProperties } = useExpose();

exposeProperties({
  inputElement: {
    configurable: true,
    get: () => input.value,
  },
  value: {
    configurable: true,
    get: () => model.value,
    set: setText,
  },
  defaultValue: {
    configurable: true,
    get: () => defaultValue.value,
  },
});

// The only paths that emit `input`, matching a native control, which reports user input alone: a
// value the attribute or the property carries in is mirrored silently.
function onInput(event) {
  setUserText(event.target.value);
  editedSinceFocus = true;
  // The post-flush effect below runs after this dispatch, so the mirror is applied here too: a
  // listener reads the host's value, form entry, and validity as one coherent state.
  mirrorValidity();
  emitBubbling("input", model.value);
}

// The inner input sits in the shadow root, where the outer form cannot see it, so the element
// submits through ElementInternals. The value is a string throughout, so an empty field submits ""
// the way a native input does instead of dropping out of the FormData.
const { internals, setValidity, emitChange, mirrorFormValue, onFormReset } = useForm();
const formValue = computed(() => String(model.value ?? ""));

// The focusable control lives in the shadow root, where the <label> naming the host does not
// reach it. The labels are read once the element has rendered, by which time the markup around
// it has been parsed.
const hostLabels = ref([]);
onMounted(() => (hostLabels.value = [...(internals?.labels ?? [])]));

// The rendered label is named here rather than through the `for` reference it also carries:
// `useAriaLabel` owns the input's `aria-labelledby`, clearing it whenever it has no elements of its
// own. The reference still forwards the click.
const renderedLabel = useTemplateRef("labelElement");
const labelElements = computed(() => [renderedLabel.value, ...hostLabels.value].filter(Boolean));

const { ariaLabel } = useAriaLabel(input, {
  label: toRef(props, "ariaLabel"),
  labelledby: toRef(props, "ariaLabelledby"),
  elements: labelElements,
});

mirrorFormValue(formValue);

onFormReset(reset);

// The inner input runs the real constraint validation — `required`, and the type-specific rules
// `type="email"` and friends bring — so its ValidityState is mirrored onto the host rather than
// recomputed. Without this the host is always valid and nothing blocks the form's submit.
watchPostEffect(() => {
  // Tracked so the mirror re-runs when any of them moves.
  void [model.value, required.value, readonly.value, props.type, props.customValidity];

  mirrorValidity();
});

function mirrorValidity() {
  const element = input.value;

  if (!element) {
    return;
  }

  element.setCustomValidity("");

  // A persistent `customValidity` must not force an otherwise-valid field invalid; it words the
  // failure of a field that is already failing.
  if (props.customValidity && !element.validity.valid) {
    element.setCustomValidity(props.customValidity);
  }

  setValidity(validityFlags(element.validity), element.validationMessage, element);
}

/**
 * @param {ValidityState} validity
 * @returns {Partial<Record<keyof ValidityStateFlags, boolean>>}
 */
function validityFlags(validity) {
  const flags = {};

  for (const flag of VALIDITY_FLAGS) {
    if (validity[flag]) {
      flags[flag] = true;
    }
  }

  return flags;
}

const MESSAGES_ID = "sp-text-field-messages";

const isFocused = ref(false);

// A message displaces the hint, which otherwise shows only while the field has focus. An empty
// `messages` says nothing, so the hint still gets its turn. The area is absent when neither has
// anything to say, so the field reserves no space below it.
const describedBy = computed(() => (shownMessages.value.length > 0 ? MESSAGES_ID : undefined));

const shownMessages = computed(() => {
  const messages = toArray(props.messages ?? [])
    .filter(Boolean)
    .map(String);

  if (messages.length > 0) {
    return messages;
  }

  if (props.hint && (toBoolean(props.persistentHint) || isFocused.value)) {
    return [props.hint];
  }

  return [];
});
let editedSinceFocus = false;
let valueAtFocus = "";

function onFocus() {
  isFocused.value = true;
  editedSinceFocus = false;
  valueAtFocus = formValue.value;
}

// A native control reports a change when the user leaves a field they changed, and stays silent
// for a value a script wrote while the field held focus.
function onBlur() {
  isFocused.value = false;

  if (editedSinceFocus && formValue.value !== valueAtFocus) {
    emitChange();
  }

  editedSinceFocus = false;
}

const isClearVisible = computed(() => {
  // A disabled field cannot be edited, and clearing is an edit. Covers an ancestor <fieldset disabled>
  // too, which never reaches the control inside the shadow root on its own.
  if (disabled.value) {
    return false;
  }

  // If the field is not clearable or the model is empty, we don't show the clear button.
  const shouldShowClear = clearable.value && model.value?.length > 0;

  // Return early if we don't need to show the clear button.
  if (!shouldShowClear) {
    return false;
  }

  // If the field is persistent clear, we always show the clear button. Otherwise, we show it when hovered.
  return persistentClear.value || isHovered.value;
});

function onPrependClick() {
  emit("click-prepend");
}

function onAppendClick(e) {
  if (!propagateAppendClick.value) {
    e.stopPropagation();
  }
  emit("click-append");
}

function onClearClick() {
  if (disabled.value) {
    return;
  }

  setUserText("");
  editedSinceFocus = true;
  // Assigning the model only schedules Vue's patch, and the mirror below reads the inner input's
  // own ValidityState — so the field is emptied here, the way a native clear does, before the
  // validity is read and the event dispatched.
  if (input.value) {
    input.value.value = "";
  }
  mirrorValidity();
  emitBubbling("input", model.value);

  // Pressing the control takes focus out of the input, so a blurred field has no later blur to
  // commit on and reports the clear here. While focus stayed in the input the usual deferred
  // commit still applies, so leaving the field reports once.
  if (!isFocused.value) {
    editedSinceFocus = false;
    valueAtFocus = formValue.value;
    emitChange();
  }

  emit("click-clear");
}

const { isSmScreen } = useBreakpoints();

const classModifiers = computed(() => [
  {
    "--readonly": readonly.value,
    "--is-focused": isFocused.value,
    "--is-invalid": false, // TODO @sleistner: Implement validation
    "--density-compact": toBoolean(props.densitysmcompact) && isSmScreen.value,
    [`--density-${props.density}`]: props.density !== "default",
    [`--variant-${props.variant}`]: props.variant !== "default",
  },
]);
</script>

<style lang="scss">
:host {
  display: block;
  box-sizing: border-box;
}
:host(:focus) {
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
}

// Disabled styling is driven by the native :disabled state (own attribute or an
// ancestor <fieldset disabled>), no JS required. The inner input's non-interactivity
// is handled separately via the bound disabled prop.
:host(:disabled) {
  cursor: not-allowed;
  opacity: 0.6;
}

slot[name="clear"] {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>

<style lang="scss" scoped>
.sp-text-field {
  display: block;
}

sp-label {
  margin-bottom: var(--sp-comp-text-field-label-margin-bottom, var(--sp-ref-scale-6));
  --sp-comp-text-label-short: var(--sp-comp-text-field-label-font, var(--sp-sys-form-field-label-short));
}

// Absent rather than empty when there is nothing to say, so a field with neither hint nor message
// takes no space below it.
.sp-text-field-messages {
  display: flex;
  flex-direction: column;
  gap: var(--sp-comp-text-field-message-gap, var(--sp-sys-form-field-message-gap));
  margin-top: var(--sp-comp-text-field-message-margin-top, var(--sp-sys-form-field-message-margin-top));
  color: var(--sp-comp-text-field-message-color, var(--sp-sys-form-field-message-color));
  font-size: var(--sp-comp-text-field-message-font-size, var(--sp-sys-form-field-message-font-size));
  line-height: var(--sp-comp-text-field-message-line-height, var(--sp-sys-form-field-message-line-height));
}

.clear-button::part(icon) {
  --fill: v-bind(clearIconFill);
}
.sp-text-field__control {
  --border-color: var(--sp-comp-text-field-border-color, var(--sp-sys-form-field-border-color, #ccc));
  --border-width: var(--sp-comp-text-field-border-width, var(--sp-sys-form-field-border-width, 1px));
  --text-field-default-height: var(--sp-comp-text-field-min-height, var(--sp-sys-form-field-min-height, 3rem));
  --text-field-height: var(--text-field-default-height);
  --text-field-default-padding-inline: var(
    --sp-comp-text-field-padding-inline,
    var(--sp-sys-form-field-padding-inline, 1rem)
  );
  --text-field-padding-inline: var(--text-field-default-padding-inline);
  --text-field-default-padding-block: var(
    --sp-comp-text-field-padding-block,
    var(--sp-sys-form-field-padding-block, 0)
  );
  --text-field-padding-block: var(--text-field-default-padding-block);

  border-width: var(--border-width);
  border-style: var(--sp-comp-text-field-border-style, var(--sp-sys-form-field-border-style, solid));
  border-color: var(--border-color);
  border-radius: var(--sp-comp-text-field-border-radius, var(--sp-sys-form-field-border-radius, 0.3rem));
  box-sizing: border-box;
  outline: none;
  outline-offset: var(--sp-comp-text-field-outline-offset, var(--sp-sys-form-field-outline-offset, 0));
  overflow: hidden;
  position: relative;
  width: 100%;
  min-width: var(--sp-comp-text-field-min-width, 10ch);
  height: var(--text-field-height);
  background-color: var(--sp-comp-text-field-background-color, var(--sp-sys-form-field-background-color, #fff));

  &.--is-focused {
    --border-color: var(
      --sp-comp-text-field-focus-border-color,
      var(--sp-sys-form-field-focus-border-color, var(--sp-sys-color-primary))
    );
    box-shadow: var(--sp-comp-text-field-focus-box-shadow, var(--sp-sys-form-field-focus-box-shadow, none));
    outline: var(--sp-comp-text-field-focus-outline, var(--sp-sys-form-field-focus-outline, none));
    outline-offset: var(--sp-comp-text-field-focus-outline-offset, var(--sp-sys-form-field-focus-outline-offset, 0));
  }

  &.--is-invalid {
    --border-color: var(
      --sp-comp-text-field-invalid-border-color,
      var(--sp-sys-form-field-invalid-border-color, var(--sp-sys-color-error))
    );
    box-shadow: var(--sp-comp-text-field-invalid-box-shadow, var(--sp-sys-form-field-invalid-box-shadow, none));
    outline: var(--sp-comp-text-field-invalid-outline, var(--sp-sys-form-field-invalid-outline, none));
    outline-offset: var(
      --sp-comp-text-field-invalid-outline-offset,
      var(--sp-sys-form-field-invalid-outline-offset, 0)
    );
  }

  &.--density-compact {
    --text-field-height: var(
      --sp-comp-text-field-compact-min-height,
      var(--sp-sys-form-field-compact-min-height, var(--text-field-default-height))
    );
    --text-field-padding-inline: var(
      --sp-comp-text-field-compact-padding-inline,
      var(--sp-sys-form-field-compact-padding-inline, var(--text-field-default-padding-inline))
    );
    --text-field-padding-block: var(
      --sp-comp-text-field-compact-padding-block,
      var(--sp-sys-form-field-compact-padding-block, var(--text-field-default-padding-block))
    );
  }

  &.--variant-plain {
    --border-width: 0;
    --text-field-padding-inline: var(--sp-comp-text-field-plain-padding-inline, 0);
    --text-field-padding-block: var(--sp-comp-text-field-plain-padding-block, 0);
  }
}

.sp-text-field-content {
  box-sizing: border-box;
  position: relative;
  display: grid;
  grid-template-columns: min-content minmax(0, 1fr) min-content;
  grid-template-areas: "prepend-inner field append-inner";
  align-items: center;
  padding-inline: var(--sp-comp-text-field-content-padding-inline, 0);
  padding-block: var(--sp-comp-text-field-content-padding-block, 0);
  height: 100%;
}

.sp-text-field-prepend {
  box-sizing: border-box;
  grid-area: prepend-inner;
  min-height: 100%;
}

.sp-text-field-append-container {
  box-sizing: border-box;
  grid-area: append-inner;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 100%;
}

.sp-text-field-append,
.sp-text-field-clear {
  min-height: 100%;
}

.sp-text-field-prepend,
.sp-text-field-append {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sp-text-field-content__input-container {
  box-sizing: border-box;
  display: flex;
  flex: 1;
  align-items: center;
  position: relative;
  cursor: var(--sp-comp-text-field-cursor, text);
  height: 100%;
  min-height: 100%;
}

.sp-text-field-content__input-field {
  box-sizing: border-box;
  padding-inline: var(--text-field-padding-inline);
  padding-block: var(--text-field-padding-block);
  display: flex;
  flex: 1;
  border: none;
  outline: none;
  font-family: var(--sp-comp-text-field-font, var(--sp-sys-form-field-font, inherit));
  font-size: var(--sp-comp-text-field-font-size, var(--sp-sys-form-field-font-size, 1rem));
  background-color: transparent;
  min-width: var(--sp-comp-text-field-min-width, 10ch);
  min-height: 100%;
  width: var(--sp-comp-text-field-width, 100%);
  /* `content` makes the input hug its value where the platform supports
     `field-sizing`; elsewhere the declaration drops and the width above rules. */
  field-sizing: var(--sp-comp-text-field-field-sizing, fixed);
  color: var(--sp-comp-text-field-color, var(--sp-sys-form-field-color, inherit));
  cursor: var(--sp-comp-text-field-cursor, text);
  text-align: var(--sp-comp-text-field-text-align, var(--sp-sys-form-field-text-align, left));
  overflow: hidden;
  text-overflow: ellipsis;

  &::placeholder {
    color: var(--sp-comp-text-field-placeholder-color, var(--sp-sys-form-field-placeholder-color, inherit));
  }

  &[inputmode="none"] {
    cursor: pointer;
    pointer-events: none;
  }

  &[readonly] {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  &:focus {
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
  }
}

.sp-text-field__loader {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}
</style>
